import React from 'react';
import { useCountdownGiftCountControllerV2 } from '@apps/registry/admin/components/CountdownGiftCount/CountdownGiftCount.controllerV2';
import { LoadingState, EditState } from './components';
import { useCashFundProgress, useRegistryGiftCounts, useGenerateInitialMessage, useHandlePriceRangeClick } from './RegistryGiftCounts.controller';
import { useTranslation } from '@shared/core';
import type { Page } from './telemetry';
import { CardContainer } from './RegistryGiftAdvisor.styles';
import { Box } from '@withjoy/joykit';
import { messageKeys } from './RegistryAdvisor.utils';
import AdvisorHeader from './components/AdvisorHeader/AdvisorHeader';
import AdvisorMetrics from './components/AdvisorMetrics/AdvisorMetrics';
import AdvisorPriceRanges from './components/AdvisorPriceRanges/AdvisorPriceRanges';

interface RegistryAdvisorMobileProps {
  eventHandle: string;
  eventId: string;
  page: Page;
  onActionComplete?: () => void;
  hideGiftStatusAndPricePointCounters?: boolean;
}

export const RegistryAdvisorMobile: React.FC<RegistryAdvisorMobileProps> = ({ eventHandle, eventId, page, onActionComplete, hideGiftStatusAndPricePointCounters }) => {
  const { everything, purchased, countDownValue, handleOnGiftTypeClick } = useCountdownGiftCountControllerV2({ eventHandle, page });
  const {
    getResultsLoading,
    initialDataLoading,
    formik,
    handleOnEditClick,
    isEditing,
    completionPercent,
    targetGiftCount,
    getPriceRangeTitleAndLink,
    priceRanges,
    isInZeroState,
    formattedCompletionPercent,
  } = useRegistryGiftCounts({ eventId, page, eventHandle });

  const { cashFundTotalCount, cashFundCompletionPercent } = useCashFundProgress({ eventHandle });
  const { t } = useTranslation('sharedComponents');
  const { giftsAvailable, giftCount, cashFundCount, giftPriceBalance, reservedAndPurchased, cashFundCompleted, giftsCompleted, shopNow } = t('registryGiftAdvisorV2');
  const { handlePriceRangeClick } = useHandlePriceRangeClick(page, onActionComplete);
  const initialMessage = useGenerateInitialMessage({
    giftsCompletionPercent: completionPercent,
    cashFundCompletionPercent,
    guestCount: formik.values.guestCount || formik.initialValues.guestCount,
    targetedGiftCount: targetGiftCount,
  });

  return (
    <CardContainer backgroundColor="white">
      <AdvisorHeader
        isEditing={isEditing}
        countDownValue={countDownValue}
        guestCount={formik.values.guestCount}
        initialGuestCount={formik.initialValues.guestCount}
        handleOnEditClick={handleOnEditClick}
        isMobile={true}
      />
      {initialDataLoading ? (
        <Box padding={6}>
          <LoadingState />
        </Box>
      ) : isEditing || isInZeroState ? (
        <Box padding={6}>
          <EditState formik={formik} />
        </Box>
      ) : (
        <>
          <AdvisorMetrics
            everything={everything}
            purchased={purchased}
            getResultsLoading={getResultsLoading}
            handleOnGiftTypeClick={handleOnGiftTypeClick}
            giftsAvailable={giftsAvailable}
            reservedAndPurchased={reservedAndPurchased}
            completionPercent={completionPercent}
            formattedCompletionPercent={formattedCompletionPercent}
            giftsCompleted={giftsCompleted}
            giftCount={giftCount}
            targetGiftCount={targetGiftCount}
            cashFundCompletionPercent={cashFundCompletionPercent}
            cashFundTotalCount={cashFundTotalCount}
            cashFundCompleted={cashFundCompleted}
            cashFundCount={cashFundCount}
            messageKeys={messageKeys}
            initialMessage={initialMessage}
            isMobile={true}
            onActionComplete={onActionComplete}
            page={page}
            hideGiftStatusAndPricePointCounters={hideGiftStatusAndPricePointCounters}
          />
          {!hideGiftStatusAndPricePointCounters && (
            <AdvisorPriceRanges
              getResultsLoading={getResultsLoading}
              giftPriceBalance={giftPriceBalance}
              priceRanges={priceRanges}
              getPriceRangeTitleAndLink={getPriceRangeTitleAndLink}
              targetGiftCount={targetGiftCount}
              shopNow={shopNow}
              onPriceRangeClick={handlePriceRangeClick}
              page={page}
              isMobile={true}
            />
          )}
        </>
      )}
    </CardContainer>
  );
};

export default RegistryAdvisorMobile;
